import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { User, Product, CartItem, Order, ProfileAnalytics } from '../types';

// Query Keys
export const queryKeys = {
  user: (id: string) => ['user', id],
  userByUsername: (username: string) => ['user', 'username', username],
  products: (userId: string) => ['products', userId],
  product: (id: string) => ['product', id],
  cart: () => ['cart'],
  orders: (userId: string) => ['orders', userId],
  analytics: (userId: string) => ['analytics', userId],
};

// User Queries
export function useUser(userId: string) {
  return useQuery({
    queryKey: queryKeys.user(userId),
    queryFn: () => api.getUserById(userId),
    enabled: !!userId,
  });
}

export function useUserByUsername(username: string) {
  return useQuery({
    queryKey: queryKeys.userByUsername(username),
    queryFn: () => api.getUserByUsername(username),
    enabled: !!username,
  });
}

// Product Queries
export function useProducts(userId: string) {
  return useQuery({
    queryKey: queryKeys.products(userId),
    queryFn: () => api.getProductsByUserId(userId),
    enabled: !!userId,
  });
}

export function useProduct(productId: string) {
  return useQuery({
    queryKey: queryKeys.product(productId),
    queryFn: () => api.getProduct(productId),
    enabled: !!productId,
  });
}

// Cart Queries
export function useCartData() {
  return useQuery({
    queryKey: queryKeys.cart(),
    queryFn: () => api.getCart(),
  });
}

// Order Queries
export function useOrders(userId: string) {
  return useQuery({
    queryKey: queryKeys.orders(userId),
    queryFn: () => api.getOrdersByUserId(userId),
    enabled: !!userId,
  });
}

// Analytics Queries
export function useAnalytics(userId: string) {
  return useQuery({
    queryKey: queryKeys.analytics(userId),
    queryFn: () => api.getProfileAnalytics(userId),
    enabled: !!userId,
  });
}

// User Mutations
export function useCreateUserProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => 
      api.createUserProfile(userData),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.user(data.id), data);
      queryClient.setQueryData(queryKeys.userByUsername(data.username), data);
    },
  });
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: Partial<User> }) =>
      api.updateUserProfile(userId, updates),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.user(data.id), data);
      queryClient.setQueryData(queryKeys.userByUsername(data.username), data);
    },
  });
}

// Product Mutations
export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productData: Omit<Product, 'id' | 'productNumber' | 'analytics' | 'createdAt' | 'updatedAt'>) =>
      api.createProduct(productData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products(data.userId) });
      queryClient.setQueryData(queryKeys.product(data.id), data);
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, updates }: { productId: string; updates: Partial<Product> }) =>
      api.updateProduct(productId, updates),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.product(data.id), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.products(data.userId) });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => api.deleteProduct(productId),
    onSuccess: (_, productId) => {
      // Remove the specific product from cache
      queryClient.removeQueries({ queryKey: queryKeys.product(productId) });
      // Invalidate all products queries to refresh lists
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// Cart Mutations
export function useAddToCart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => api.addToCart(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cart() });
    },
  });
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cartItemId, quantity }: { cartItemId: string; quantity: number }) =>
      api.updateCartItemQuantity(cartItemId, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cart() });
    },
  });
}

export function useClearCart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.clearCart(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cart() });
    },
  });
}

// Order Mutations
export function useCreateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (customerData: { email?: string; name?: string; phone?: string }) =>
      api.createOrder(customerData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders(data.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cart() });
    },
  });
}

// Analytics Mutations
export function useTrackEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ 
      userId, 
      type, 
      targetId, 
      metadata 
    }: { 
      userId: string; 
      type: 'profile_view' | 'product_view' | 'product_click' | 'social_link_click'; 
      targetId?: string; 
      metadata?: Record<string, any> 
    }) => api.trackEvent(userId, type, targetId, metadata),
    onSuccess: (_, variables) => {
      // Invalidate analytics and related data
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics(variables.userId) });
      if (variables.targetId && variables.type.includes('product')) {
        queryClient.invalidateQueries({ queryKey: queryKeys.product(variables.targetId) });
      }
    },
  });
}
